<template>
  <header-block>
    <!-- Top-right save, consistent with the other editor pages. Disabled
         until the active file has changes; Revert stays inline below. -->
    <button
      class="btn btn-primary"
      :disabled="saving || !dirtyCurrent"
      :title="!dirtyCurrent ? $t('seo_files.nothing_to_save') : $t('seo_files.unsaved')"
      v-on:click="save"
    >
      {{ saving ? $t("seo_files.save_saving") : $t("seo_files.save") }}
      <span v-if="dirtyCurrent && !saving" class="save-dot"></span>
    </button>
  </header-block>
  <main class="seo-page seo-files-page">
    <section class="seo-hero compact">
      <div>
        <span class="eyebrow">{{ $t("seo_files.crawlers") }}</span>
        <h1>{{ $t("seo_files.title") }}</h1>
        <p>{{ $t("seo_files.intro") }}</p>
      </div>
      <a
        v-if="liveFileUrl"
        class="device-toggle single"
        :href="liveFileUrl"
        target="_blank"
        rel="noopener"
        >{{ $t("seo_files.open_live") }}</a
      >
    </section>

    <section v-if="loading" class="panel muted">{{ $t("loading") }}</section>

    <section v-else-if="!domainData" class="panel">
      {{ $t("seo_files.no_domains") }}
    </section>

    <template v-else>
      <section class="panel seo-files-bar">
        <label class="seo-files-select">
          <span>{{ $t("seo_files.domain") }}</span>
          <select v-model="domainId">
            <option v-for="d in domains" :key="d.id" :value="d.id">
              {{ d.domain }}
            </option>
          </select>
        </label>
        <div class="seo-files-tabs" role="tablist">
          <button
            v-for="file in fileTabs"
            :key="file"
            role="tab"
            :class="{ active: activeFile === file }"
            v-on:click="switchFile(file)"
          >
            {{ file }}
            <span v-if="dirtyFor(fileKey(file))" class="dot"></span>
          </button>
        </div>
      </section>

      <section class="panel">
        <div class="panel-head">
          <div>
            <span class="eyebrow">{{ activeFile }}</span>
            <h2>{{ $t("seo_files.files." + activeFileKey + "_title") }}</h2>
          </div>
          <div class="mode-toggle">
            <button
              v-for="mode in fileModes"
              :key="mode"
              :class="{ active: current.mode === mode }"
              v-on:click="setMode(mode)"
            >
              {{ $t("seo_files.modes." + mode) }}
              <span v-if="current.mode === mode" class="mode-check">✓</span>
            </button>
          </div>
        </div>

        <!-- default -->
        <div v-if="current.mode === 'default'" class="seo-block">
          <span class="badge-default">{{ $t("seo_files.using_default") }}</span>
          <p class="muted">
            {{ $t("seo_files.default_hint_" + activeFileKey) }}
          </p>
          <pre class="seo-preview">{{ defaultPreview }}</pre>
          <button class="btn-outline" v-on:click="startFromDefault">
            {{ $t("seo_files.start_from_default") }}
          </button>
        </div>

        <!-- custom (visual rule builder, robots only) -->
        <div v-else-if="current.mode === 'custom'" class="seo-block">
          <p class="muted">{{ $t("seo_files.custom_hint") }}</p>

          <div class="rule-presets">
            <button
              v-for="preset in robotPresets"
              :key="preset.key"
              class="btn-outline preset-chip"
              v-on:click="applyPreset(preset.key)"
            >
              {{ $t("seo_files.presets." + preset.key) }}
            </button>
          </div>

          <article
            v-for="(group, gi) in current.rules.groups"
            :key="'g' + gi"
            :class="{ 'rule-group': true, closed: editingGroup !== gi }"
          >
            <div class="rule-group-head">
              <span class="eyebrow"
                >{{ $t("seo_files.bot_group") }} {{ gi + 1 }}</span
              >
              <span
                v-if="editingGroup !== gi"
                class="muted group-summary"
                >{{ groupSummary(group) }}</span
              >
              <button class="btn-link" v-on:click="toggleGroupEdit(gi)">
                {{
                  editingGroup === gi
                    ? $t("seo_files.group_close")
                    : $t("seo_files.group_edit")
                }}
              </button>
              <button class="btn-link danger" v-on:click="removeGroup(gi)">
                {{ $t("seo_files.remove_group") }}
              </button>
            </div>
            <div class="agent-chips" v-if="editingGroup === gi">
              <div
                v-for="category in botCategories"
                :key="category.key"
                class="agent-category"
              >
                <span class="agent-cat-label">{{
                  $t("seo_files.bot_groups." + category.key)
                }}</span>
                <button
                  v-for="bot in category.bots"
                  :key="bot.value"
                  :title="bot.hint"
                  :class="{
                    chip: true,
                    selected: group.agents.includes(bot.value),
                  }"
                  v-on:click="toggleAgent(group, bot.value)"
                >
                  {{ bot.label }}
                </button>
              </div>
              <div class="agent-category">
                <span class="agent-cat-label">{{
                  $t("seo_files.bot_groups.custom")
                }}</span>
                <button
                  v-for="agent in customAgents(group)"
                  :key="'c' + agent"
                  class="chip selected custom-chip"
                  v-on:click="removeAgent(group, agent)"
                  :title="$t('seo_files.custom_bot_remove')"
                >
                  {{ agent }} ✕
                </button>
                <input
                  v-model="customDraft"
                  class="custom-bot-input"
                  :placeholder="$t('seo_files.custom_bot')"
                  spellcheck="false"
                  v-on:keyup.enter="addCustomAgent(group)"
                />
                <button
                  class="btn-link"
                  v-on:click="addCustomAgent(group)"
                >
                  {{ $t("seo_files.custom_bot_add") }}
                </button>
              </div>
            </div>

            <div class="rule-rows" v-if="editingGroup === gi">
              <div
                v-for="(rule, ri) in group.rules"
                :key="'r' + ri"
                class="rule-row"
              >
                <select
                  v-model="rule.action"
                  :aria-label="$t('seo_files.rule_action')"
                >
                  <option value="disallow">
                    {{ $t("seo_files.actions.disallow") }}
                  </option>
                  <option value="allow">
                    {{ $t("seo_files.actions.allow") }}
                  </option>
                </select>
                <input v-model="rule.path" spellcheck="false" />
                <button
                  class="btn-link danger"
                  v-on:click="group.rules.splice(ri, 1)"
                >
                  {{ $t("seo_files.remove_rule") }}
                </button>
              </div>
              <button class="btn-link" v-on:click="addRule(group)">
                {{ $t("seo_files.add_rule") }}
              </button>
            </div>
          </article>

          <button class="btn-outline" v-on:click="addGroup">
            {{ $t("seo_files.add_group") }}
          </button>

          <div class="panel-head">
            <div>
              <span class="eyebrow">{{ $t("seo_files.preview") }}</span>
            </div>
          </div>
          <pre class="seo-preview">{{ generatedText }}</pre>
        </div>

        <!-- expert -->
        <div v-else class="seo-block">
          <p class="muted">
            {{ $t("seo_files.expert_hint_" + activeFileKey) }}
          </p>
          <CodeMirror v-model="current.text" />
        </div>

        <div class="seo-files-actions" v-if="dirtyCurrent">
          <button class="btn-outline" v-on:click="revert">
            {{ $t("seo_files.revert") }}
          </button>
          <span class="muted">{{ $t("seo_files.unsaved") }}</span>
        </div>
      </section>
    </template>

    <transition name="fade">
      <div
        v-if="confirmBox.open"
        class="safe-modal-backdrop"
        v-on:click.self="confirmBox.resolve(false)"
      >
        <div class="safe-modal">
          <h3>{{ $t("seo_files." + confirmBox.key + "_title") }}</h3>
          <p class="muted">{{ $t("seo_files." + confirmBox.key + "_body") }}</p>
          <div class="safe-modal-actions">
            <button class="btn-outline" v-on:click="confirmBox.resolve(false)">
              {{ $t("seo_files.cancel") }}
            </button>
            <button
              class="btn-process-help"
              v-on:click="confirmBox.resolve(true)"
            >
              {{ $t("seo_files.confirm") }}
            </button>
          </div>
        </div>
      </div>
    </transition>
  </main>
</template>

<script>
import headerBlock from "../components/header.vue";
import CodeMirror from "../components/CodeMirror.vue";
import http from "../modules/http";

const STAMP = "# managed by prodyflow";

const BOT_CATEGORIES = [
  {
    key: "search",
    bots: [
      { value: "googlebot", label: "Googlebot" },
      { value: "bingbot", label: "Bingbot" },
      { value: "duckduckbot", label: "DuckDuckGo" },
      { value: "yandex", label: "Yandex" },
      { value: "applebot", label: "Applebot" },
      { value: "baiduspider", label: "Baidu" },
      { value: "sogou", label: "Sogou" },
      { value: "mojeekbot", label: "Mojeek" },
    ],
  },
  {
    key: "ai",
    bots: [
      { value: "gptbot", label: "GPTBot", hint: "OpenAI" },
      { value: "oai-searchbot", label: "OAI-SearchBot", hint: "OpenAI" },
      { value: "chatgpt-user", label: "ChatGPT-User", hint: "OpenAI" },
      { value: "ccbot", label: "CCBot" },
      { value: "claudebot", label: "ClaudeBot" },
      { value: "claude-web", label: "Claude-Web" },
      { value: "anthropic-ai", label: "anthropic-ai" },
      { value: "google-extended", label: "Google-Extended" },
      { value: "perplexitybot", label: "PerplexityBot" },
      { value: "bytespider", label: "Bytespider", hint: "ByteDance" },
      { value: "applebot-extended", label: "Applebot-Extended" },
      { value: "facebookbot", label: "FacebookBot" },
      { value: "cohere-ai", label: "cohere-ai" },
      { value: "diffbot", label: "Diffbot" },
      { value: "omgilibot", label: "omgilibot" },
    ],
  },
  {
    key: "other",
    bots: [
      { value: "*", label: "* (all)", hint: "seo_files.bot_all" },
      { value: "facebookexternalhit", label: "Facebook" },
      { value: "slackbot", label: "Slack" },
      { value: "twitterbot", label: "Twitter/X" },
      { value: "linkedinbot", label: "LinkedIn" },
      { value: "pinterestbot", label: "Pinterest" },
      { value: "ahrefsbot", label: "Ahrefs", hint: "seo_tools" },
      { value: "semrushbot", label: "Semrush", hint: "seo_tools" },
    ],
  },
];

const CATALOG_VALUES = BOT_CATEGORIES.flatMap((c) => c.bots.map((b) => b.value));
const AI_BOTS = BOT_CATEGORIES.find((c) => c.key === "ai").bots.map((b) => b.value);

const PRESETS = [
  {
    key: "block_ai",
    groups: [{ agents: AI_BOTS, rules: [{ action: "disallow", path: "/" }] }],
  },
  {
    key: "block_all",
    groups: [{ agents: [], rules: [{ action: "disallow", path: "/" }] }],
  },
];

export default {
  components: { headerBlock, CodeMirror },
  data() {
    return {
      domains: [],
      domainId: null,
      domainData: null,
      loading: true,
      activeFile: "robots.txt",
      fileTabs: ["robots.txt", "llms.txt"],
      active: { robots: this.emptyState(), llms: this.emptyState() },
      saved: { robots: this.emptyState(), llms: this.emptyState() },
      confirmBox: {
        open: false,
        key: "",
        resolve: null,
      },
      customDraft: "",
      saving: false,
      // which bot-group card is open for editing; the others show a summary
      editingGroup: null,
    };
  },
  computed: {
    botCategories() {
      return BOT_CATEGORIES;
    },
    robotPresets() {
      return PRESETS.map((p) => ({ key: p.key }));
    },
    activeFileKey() {
      return this.activeFile === "robots.txt" ? "robots" : "llms";
    },
    fileModes() {
      return this.activeFileKey === "robots"
        ? ["default", "custom", "expert"]
        : ["default", "expert"];
    },
    current() {
      return this.active[this.activeFileKey];
    },
    dirtyCurrent() {
      return this.dirtyFor(this.activeFileKey);
    },
    generatedText() {
      return this.serializeRules(this.current.rules) + this.rulesFooter();
    },
    defaultPreview() {
      if (this.activeFileKey === "llms") {
        return this.renderedDefaultLlms();
      }
      return "User-agent: *\nAllow: /\n" + this.rulesFooter();
    },
    liveFileUrl() {
      const domain = this.domainData?.domain;
      return domain ? "https://" + domain + "/" + this.activeFile : null;
    },
  },
  methods: {
    // mirrors SitemapController::protectedRobots — this block and the
    // sitemap line are always appended by the server, never saved in the DB
    rulesFooter() {
      return (
        "\n\n# " +
        this.$t("seo_files.protected_note") +
        "\nUser-agent: *\nDisallow: /cart\nDisallow: /profile\n" +
        "Disallow: /payment\nDisallow: /order\nDisallow: /invoice\n\n" +
        "Sitemap: https://" +
        (this.domainData?.domain ?? "{domain}") +
        "/sitemap.xml"
      );
    },
    emptyState() {
      return { mode: "default", rules: { groups: [] }, text: "" };
    },
    fileKey(file) {
      return file === "robots.txt" ? "robots" : "llms";
    },
    dirtyFor(key) {
      const a = this.active[key];
      const s = this.saved[key];
      if (a.mode !== s.mode) return true;
      if ((a.text || "") !== (s.text || "")) return true;
      if (JSON.stringify(a.rules) !== JSON.stringify(s.rules)) return true;
      return false;
    },
    confirm(key) {
      this.confirmBox.key = key;
      this.confirmBox.open = true;
      return new Promise(
        (resolve) =>
          (this.confirmBox.resolve = (result) => {
            this.confirmBox.open = false;
            resolve(result);
          })
      );
    },
    async setMode(mode) {
      if (mode === this.current.mode) return;

      if (mode === "expert") {
        // Seamless: rules are serialized into the text, but they stay in the
        // state — going back to Custom parses the (stamped) text again.
        this.current.text = this.serializeRules(this.current.rules);
        this.current.mode = mode;
        return;
      }
      if (mode === "custom") {
        // stamped text round-trips losslessly through the parser
        if (this.isStamped(this.current.text)) {
          this.current.rules = this.parseRules(this.current.text);
        } else if (this.current.text) {
          // hand-written file — the rule builder cannot keep every directive
          const proceed = await this.confirm("reformat");
          if (!proceed) return;
          this.current.rules = { groups: [] };
        } else if (!this.current.rules.groups.length) {
          this.current.rules = { groups: [{ agents: [], rules: [] }] };
        }
        this.current.mode = mode;
        this.editingGroup = null;
        this.warnIfBlocking();
        return;
      }
      if (mode === "default" && this.dirtyFor(this.activeFileKey)) {
        const proceed = await this.confirm("discard");
        if (!proceed) return;
      }
      this.current.mode = mode;
    },
    warnIfBlocking() {
      const blocksAll = this.current.rules.groups.some(
        (g) =>
          (!g.agents.length || g.agents.includes("*")) &&
          g.rules.some((r) => r.action === "disallow" && r.path === "/")
      );
      if (blocksAll) {
        this.confirm("block_all");
      }
    },
    isStamped(text) {
      return (
        (text || "").split("\n")[0]?.trim().toLowerCase() ===
        STAMP.toLowerCase()
      );
    },
    parseRules(text) {
      const groups = [];
      let group = null;
      for (const raw of (text || "").split("\n")) {
        const line = (raw.split("#")[0] ?? "").trim();
        if (!line) continue;
        const m = line.match(/^([a-z-]+)\s*:\s*(.*)$/i);
        if (!m) continue;
        const directive = m[1].toLowerCase();
        const value = m[2].trim();
        if (directive === "user-agent") {
          if (!group || group.rules.length) {
            group = { agents: [], rules: [] };
            groups.push(group);
          }
          group.agents.push(value.toLowerCase());
          continue;
        }
        if (directive === "disallow" || directive === "allow") {
          if (group) group.rules.push({ action: directive, path: value });
          continue;
        }
        // Sitemap/Crawl-delay handled elsewhere, unknown directives kept raw
        if (directive !== "sitemap" && directive !== "crawl-delay" && group) {
          group.rules.push({ action: "raw:" + directive, path: value });
        }
      }
      return { groups: groups.length ? groups : [{ agents: [], rules: [] }] };
    },
    serializeRules(rules) {
      const lines = [STAMP];
      for (const group of rules.groups) {
        for (const agent of group.agents.length ? group.agents : ["*"]) {
          lines.push("User-agent: " + agent);
        }
        if (!group.rules.length) {
          lines.push("Allow: /");
        }
        for (const rule of group.rules) {
          if (rule.action.startsWith("raw:")) {
            lines.push(rule.action.substring(4) + ": " + rule.path);
            continue;
          }
          lines.push(
            rule.action.charAt(0).toUpperCase() +
              rule.action.slice(1) +
              ": " +
              rule.path
          );
        }
        lines.push("");
      }
      return lines.join("\n");
    },
    toggleAgent(group, botValue) {
      const i = group.agents.indexOf(botValue);
      if (i === -1) {
        if (botValue === "*") {
          group.agents = ["*"];
        } else {
          const has = group.agents.indexOf("*");
          if (has !== -1) group.agents.splice(has, 1);
          group.agents.push(botValue);
        }
      } else {
        group.agents.splice(i, 1);
      }
    },
    // agents picked in the Custom field that are not in any catalog chip
    customAgents(group) {
      return group.agents.filter((a) => !CATALOG_VALUES.includes(a));
    },
    addCustomAgent(group) {
      const agent = String(this.customDraft || "")
        .trim()
        .toLowerCase();
      if (!agent) return;
      if (!/^[a-z0-9_*.-]+$/.test(agent)) return;
      if (!group.agents.includes(agent)) {
        group.agents.push(agent);
      }
      this.customDraft = "";
    },
    removeAgent(group, agent) {
      const i = group.agents.indexOf(agent);
      if (i !== -1) group.agents.splice(i, 1);
    },
    isEditingGroup(gi) {
      return this.editingGroup === gi;
    },
    toggleGroupEdit(gi) {
      this.editingGroup = this.editingGroup === gi ? null : gi;
    },
    // compact one-liner for a closed group card
    groupSummary(group) {
      const labelOf = (agent) => {
        for (const category of BOT_CATEGORIES) {
          const bot = category.bots.find((b) => b.value === agent);
          if (bot) return bot.label;
        }
        return agent;
      };
      const parts = [];
      if (group.agents.length) {
        parts.push(group.agents.map(labelOf).join(", "));
      } else {
        parts.push("* (all)");
      }
      if (group.agents.length && group.agents[0] === "*") {
        return parts[0];
      }
      if (!group.rules.length) {
        parts.push(this.$t("seo_files.group_no_rules"));
      } else {
        parts.push(
          this.$t("seo_files.group_rules", { n: group.rules.length })
        );
      }
      return parts.join(" — ");
    },
    addGroup() {
      this.current.rules.groups.push({ agents: [], rules: [] });
      this.editingGroup = this.current.rules.groups.length - 1;
    },
    removeGroup(gi) {
      this.current.rules.groups.splice(gi, 1);
      if (this.editingGroup === gi) {
        this.editingGroup = null;
      } else if (this.editingGroup !== null && this.editingGroup > gi) {
        this.editingGroup -= 1;
      }
    },
    addRule(group) {
      group.rules.push({ action: "disallow", path: "/" });
    },
    applyPreset(key) {
      const preset = PRESETS.find((p) => p.key === key);
      if (preset) {
        this.current.rules.groups.push(
          ...JSON.parse(JSON.stringify(preset.groups))
        );
        this.editingGroup = this.current.rules.groups.length - 1;
      }
    },
    startFromDefault() {
      if (this.activeFileKey === "robots") {
        this.current.rules = { groups: [] };
        this.setMode("custom");
      } else {
        this.current.text = this.renderedDefaultLlms();
        this.setMode("expert");
      }
    },
    renderedDefaultLlms() {
      const host = this.domainData?.domain ?? "";
      return (
        "# " +
        host +
        "\n\n> " +
        this.$t("seo_files.llms_default_preview", { domain: host }) +
        "\n\n- Sitemap: https://" +
        host +
        "/sitemap.xml\n- Robots: https://" +
        host +
        "/robots.txt\n"
      );
    },
    async switchFile(file) {
      if (file === this.activeFile) return;
      if (this.dirtyFor(this.activeFileKey)) {
        const proceed = await this.confirm("unsaved");
        if (!proceed) return;
        this.revert();
      }
      this.activeFile = file;
    },
    async loadDomains() {
      try {
        const data = await http.fetch("/seo/domains");
        this.domains = data.domains ?? [];
        // a single domain is selected automatically; with multiple domains
        // the user picks one and the watcher loads it
        if (!this.domainId && this.domains.length === 1) {
          await this.loadDomain(this.domains[0].id);
          this.domainId = this.domains[0].id;
        }
      } finally {
        this.loading = false;
      }
    },
    async loadDomain(id) {
      this.loading = true;
      this.editingGroup = null;
      this.customDraft = "";
      try {
        const data = await http.fetch("/seo/domains/" + id + "/seo-files");
        this.domainData = data.domain;
        this.saved = {
          robots: {
            mode: data.domain.robots_mode || "default",
            rules: this.parseRules(data.domain.robots_txt || ""),
            text: data.domain.robots_txt || "",
          },
          llms: {
            mode: data.domain.llms_mode || "default",
            rules: { groups: [] },
            text: data.domain.llms_txt || "",
          },
        };
        this.active = JSON.parse(JSON.stringify(this.saved));
      } finally {
        this.loading = false;
      }
    },
    async save() {
      if (this.saving) return;
      this.saving = true;
      const key = this.activeFileKey;
      const payload = {
        robots_mode:
          key === "robots" ? this.active.robots.mode : this.saved.robots.mode,
        llms_mode:
          key === "llms" ? this.active.llms.mode : this.saved.llms.mode,
      };
      if (key === "robots") {
        payload.robots_txt =
          this.active.robots.mode === "custom"
            ? this.serializeRules(this.active.robots.rules)
            : this.active.robots.text;
        payload.llms_txt = this.saved.llms.text;
      } else {
        payload.llms_txt = this.active.llms.text;
        payload.robots_txt =
          this.saved.robots.mode === "custom"
            ? this.serializeRules(this.saved.robots.rules)
            : this.saved.robots.text;
      }
      try {
        const data = await http.fetch(
          "/seo/domains/" + this.domainId + "/seo-files",
          payload,
          false,
          "PUT"
        );
        this.domainData = data.domain;
        this.saved = {
          robots: {
            mode: data.domain.robots_mode,
            rules: this.parseRules(data.domain.robots_txt || ""),
            text: data.domain.robots_txt || "",
          },
          llms: {
            mode: data.domain.llms_mode,
            rules: { groups: [] },
            text: data.domain.llms_txt || "",
          },
        };
        this.active = JSON.parse(JSON.stringify(this.saved));
      } catch (e) {
        // error panel is handled centrally by the http module
      } finally {
        this.saving = false;
      }
    },
    revert() {
      this.active[this.activeFileKey] = JSON.parse(
        JSON.stringify(this.saved[this.activeFileKey])
      );
    },
  },
  watch: {
    async domainId(id, prev) {
      if (prev !== null && this.dirtyCurrent) {
        const proceed = await this.confirm("unsaved");
        if (!proceed) {
          this.domainId = this.domainData.id;
          return;
        }
      }
      if (id !== null) {
        this.activeFile = "robots.txt";
        await this.loadDomain(id);
      }
    },
  },
  mounted() {
    this.loadDomains();
  },
};
</script>

<style scoped>
/* layout & hero — mirrors PluginsPage's SEO page look. Those are scoped
   there, so each SEO sub-page carries its own copy. */
.seo-page {
  max-width: 1180px;
  margin: 0 auto;
  padding: 34px 22px 60px;
  color: #172033;
}
.seo-hero {
  display: flex;
  justify-content: space-between;
  align-items: end;
  gap: 20px;
  margin-bottom: 26px;
  padding: 28px 30px;
  border-radius: 20px;
  color: #fff;
  background: linear-gradient(120deg, #172554, #4c1d95 58%, #7c3aed);
  box-shadow: 0 16px 40px rgba(49, 46, 129, 0.18);
}
.seo-hero .device-toggle.single {
  background: #fff;
  color: #312e81;
  padding: 10px 18px;
  text-decoration: none;
  font-size: 12px;
  font-weight: 700;
  border-radius: 9px;
}
.seo-hero h1 {
  margin: 5px 0 8px;
  font-size: 30px;
  letter-spacing: -0.8px;
  color: #fff;
}
.seo-hero p {
  margin: 0;
  color: #ddd6fe;
  max-width: 560px;
}
.eyebrow {
  display: block;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 10px;
  font-weight: 800;
  color: #8b5cf6;
}
.seo-hero .eyebrow {
  color: #c4b5fd;
}
.panel,
.integration-card {
  background: #fff;
  border: 1px solid #e9e7f2;
  border-radius: 16px;
  box-shadow: 0 5px 18px rgba(30, 27, 75, 0.045);
}
.panel-head {
  display: flex;
  justify-content: space-between;
  align-items: start;
  gap: 10px;
  margin-bottom: 18px;
  padding: 20px 20px 0;
}
.panel-head > div {
  padding: 0;
}
.panel {
  padding: 18px 20px;
}
.panel h2 {
  margin: 5px 0 0;
  font-size: 17px;
}
.muted {
  font-size: 11px;
  color: #98a1b2;
}
.empty-state,
.seo-files-entry {
  color: #98a1b2;
  font-size: 12px;
  padding: 6px 0;
}
.integration-card {
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 17px;
}
.integration-card h3 {
  margin: 0 0 4px;
  font-size: 15px;
}
.integration-card p {
  margin: 0;
  color: #7c8498;
  font-size: 12px;
}
.integration-card .btn-outline {
  text-decoration: none;
  white-space: nowrap;
}
.btn-outline {
  border: 1px solid #ddd9ea;
  background: #fff;
  color: #4c1d95;
  border-radius: 8px;
  padding: 7px 10px;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
  text-decoration: none;
  display: inline-block;
}
.btn-outline:disabled {
  color: #a1a1aa;
  border-color: #e4e4e7;
  cursor: default;
}
.btn-process-help {
  background: linear-gradient(120deg, #172554, #4c1d95 58%, #7c3aed);
  color: #fff;
  border: 0;
  border-radius: 8px;
  padding: 9px 18px;
  font-size: 12.5px;
  font-weight: 700;
  cursor: pointer;
}
.btn-process-help:disabled {
  background: #cbd5e1;
  cursor: default;
}
@media (max-width: 560px) {
  .seo-page {
    padding: 20px 14px;
  }
  .seo-hero {
    display: block;
    padding: 22px;
  }
  .seo-hero .device-toggle.single {
    display: inline-block;
    margin-top: 16px;
  }
  .seo-files-actions {
    flex-wrap: wrap;
  }
  .rule-row {
    grid-template-columns: 110px 1fr auto;
  }
}
/* mode segmented control — light-background variant (the device-toggle in
   PluginsPage is purple-on-gradient and only works inside the dark hero) */
.mode-toggle {
  display: inline-flex;
  padding: 3px;
  border-radius: 9px;
  border: 1px solid #e9e7f2;
  background: #f5f3ff;
}
.mode-toggle button {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  border: 0;
  border-radius: 7px;
  padding: 7px 14px;
  background: transparent;
  cursor: pointer;
  font-size: 12.5px;
  font-weight: 700;
  color: #6d28d9;
  transition: background 0.15s, color 0.15s, box-shadow 0.15s;
}
.mode-toggle button:hover {
  background: #ede9fe;
}
.mode-toggle button.active {
  background: #4c1d95;
  color: #fff;
  box-shadow: 0 2px 8px rgba(76, 29, 149, 0.35);
}
.mode-check {
  font-size: 10px;
  line-height: 1;
}
/* compact hero — smaller than the SEO dashboard header: this page is a
   tool, the big gradient block is wasted space here */
.seo-hero.compact {
  padding: 16px 22px;
  margin-bottom: 18px;
  border-radius: 14px;
  box-shadow: 0 8px 22px rgba(49, 46, 129, 0.12);
  gap: 14px;
}
.seo-hero.compact h1 {
  margin: 2px 0 4px;
  font-size: 19px;
  letter-spacing: -0.3px;
}
.seo-hero.compact p {
  font-size: 12.5px;
  max-width: 520px;
}
.seo-hero.compact .eyebrow {
  font-size: 9px;
}
.seo-files-bar {
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
}
.seo-files-select {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 220px;
}
.seo-files-select span {
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  opacity: 0.7;
}
.seo-files-select select,
.rule-row input,
.rule-row select {
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  background: #fff;
}
.seo-files-tabs {
  display: inline-flex;
  margin-left: auto;
  padding: 3px;
  border-radius: 9px;
  background: #f1f5f9;
}
.seo-files-tabs button {
  border: 0;
  border-radius: 7px;
  padding: 7px 14px;
  background: transparent;
  cursor: pointer;
  font-size: 13px;
  font-weight: 700;
  color: #475569;
  font-family: ui-monospace, monospace;
}
.seo-files-tabs button.active {
  color: #fff;
  background: #312e81;
}
.seo-files-tabs .dot {
  display: inline-block;
  width: 6px;
  height: 6px;
  margin-left: 6px;
  border-radius: 50%;
  background: #f59e0b;
  vertical-align: middle;
}
.seo-block {
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding: 18px;
  border-radius: 12px;
  background: #f8fafc;
}
.badge-default {
  align-self: flex-start;
  padding: 3px 10px;
  border-radius: 20px;
  background: #dcfce7;
  color: #166534;
  font-size: 11px;
  font-weight: 700;
}
.seo-preview {
  margin: 0;
  padding: 14px;
  border-radius: 10px;
  background: #0f172a;
  color: #e2e8f0;
  font-family: ui-monospace, monospace;
  font-size: 12.5px;
  line-height: 1.6;
  white-space: pre-wrap;
  overflow: auto;
}
.rule-presets {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.preset-chip {
  font-size: 12px;
  padding: 6px 12px;
}
.rule-group {
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}
.rule-group.closed {
  background: #fcfcfd;
}
.rule-group-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  cursor: pointer;
}
.rule-group.closed .rule-group-head {
  cursor: pointer;
}
.rule-group-head .btn-link:last-child {
  flex: none;
}
.group-summary {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.agent-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}
.agent-category {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  width: 100%;
  padding: 4px 0;
}
.agent-cat-label {
  min-width: 64px;
  font-size: 10px;
  font-weight: 800;
  text-transform: uppercase;
  letter-spacing: 0.08em;
  color: #a78bfa;
}
.agent-category .chip {
  background: #f8fafc;
}
.agent-category .chip.selected {
  background: #4c1d95;
  border-color: #4c1d95;
  color: #fff;
}
.custom-chip {
  background: #ede9fe !important;
  color: #4c1d95 !important;
  border-color: #a78bfa !important;
}
.custom-bot-input {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
  padding: 5px 11px;
  font-size: 12px;
  min-width: 150px;
}
.chip {
  border: 1px solid #cbd5e1;
  border-radius: 16px;
  padding: 5px 11px;
  background: #fff;
  cursor: pointer;
  font-size: 12px;
}
.chip.selected {
  background: #4c1d95;
  border-color: #4c1d95;
  color: #fff;
}
.rule-rows {
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.rule-row {
  display: grid;
  grid-template-columns: 130px 1fr auto;
  gap: 8px;
  align-items: center;
}
.rule-row input {
  font-family: ui-monospace, monospace;
}
.btn-link {
  border: 0;
  background: none;
  cursor: pointer;
  color: #4c1d95;
  font-weight: 700;
  font-size: 12px;
  padding: 4px;
}
.btn-link.danger {
  color: #b91c1c;
}
.seo-files-actions {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-top: 18px;
}
.save-dot {
  display: inline-block;
  width: 7px;
  height: 7px;
  margin-left: 7px;
  border-radius: 50%;
  background: #fcd34d;
  vertical-align: middle;
}
.safe-modal-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 60;
}
.safe-modal {
  background: #fff;
  border-radius: 14px;
  padding: 22px;
  max-width: 460px;
  box-shadow: 0 20px 60px rgba(2, 6, 23, 0.35);
}
.safe-modal-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  margin-top: 16px;
}
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s;
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}
</style>
